/**
 * The single canonical `src/lib/api.js` shown in Zichtbaar Doc.
 * Kept as a plain string so the doc page and the starter template stay in sync.
 * The embedded code intentionally uses string concatenation (no inner template
 * literals) so it nests cleanly in this file without escaping headaches.
 * When the SDK gains a method, update it HERE and in docs/starter/api.js.
 */
export const CANONICAL_WRAPPER = `/**
 * Zichtbaar API client (canonieke versie).
 * De logica zit in de gehoste SDK (window.ZichtbaarAPI), geladen via
 * <script src="https://api.zichtbaarstudio.be/sdk.js"></script> in index.html.
 * Dit bestand is alleen configuratie + dunne proxy-functies.
 */

const CONFIG = {
  apiUrl: import.meta.env.VITE_API_URL || "https://api.zichtbaarstudio.be/api",
  companyId: import.meta.env.VITE_COMPANY_ID,
};

// Globale SDK ophalen en initialiseren.
const getSdk = () => {
  if (typeof window !== "undefined" && window.ZichtbaarAPI) {
    return window.ZichtbaarAPI.init(CONFIG);
  }
  console.error(
    "ZichtbaarAPI SDK niet geladen. Staat <script src='.../sdk.js'> in index.html?",
  );
  return null;
};

// Roep een SDK-methode aan, of geef een nette fout.
const call = (method, ...args) => {
  const sdk = getSdk();
  if (sdk && typeof sdk[method] === "function") return sdk[method](...args);
  return Promise.reject(new Error("SDK-methode '" + method + "' niet beschikbaar"));
};

// Directe fetch — werkt ook als de gecachte SDK de methode (nog) niet kent.
const fetchJson = async (path, options) => {
  const res = await fetch(CONFIG.apiUrl + path, options);
  if (!res.ok) throw new Error("Request faalde (" + res.status + "): " + path);
  return res.json();
};

// -- Lezen -----------------------------------------------------
export const getCompanyInfo = () => call("getCompanyInfo");
export const getItems = () => call("getItems");
export const getProducts = () => call("getProducts");

export const getReservationAvailability = (date) =>
  call("getReservationAvailability", date);

// Endpoints met fetch-fallback (handig bij oudere gecachte SDK):
export const getActivePopup = () =>
  fetchJson("/popups/active/" + CONFIG.companyId);

export const getJobListings = () =>
  fetchJson("/jobs/listings/" + CONFIG.companyId);

// -- Schrijven -------------------------------------------------
export const makeReservation = (data) => call("makeReservation", data);
export const createReservation = makeReservation; // alias

export const submitContactForm = (data) => call("submitContactForm", data);

export const createOrder = (data, redirectUrl) =>
  call("createOrder", data, redirectUrl);

export const submitOrderRequest = (data) => call("submitOrderRequest", data);

export const checkPaymentStatus = (orderId) =>
  call("checkPaymentStatus", orderId);

export const submitJobApplication = (data) =>
  fetchJson("/jobs/apply/" + CONFIG.companyId, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

// -- Legacy-aliassen (oudere componenten) ----------------------
export const fetchCompanyInfo = getCompanyInfo;
export const fetchCompanyItems = getItems;
export const fetchProducts = getProducts;
`;
